import { type PlanState } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { UserRole } from "@next-platform/packages-ts-types";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { getPlanDisplayColors, isExpiredPlanState } from "@/constants/planTierColors";
import { cn } from "@/utils";

const ALERT_BOLD_COMPONENT = { bold: <span className="font-semibold text-gray-12" /> };

type SubscriptionPlanGuidanceProps = {
  planRole: UserRole;
  planState: PlanState;
};

const SubscriptionPlanGuidance = ({ planRole, planState }: SubscriptionPlanGuidanceProps) => {
  const colors = getPlanDisplayColors({ role: planRole, planState });
  const isExpiredPlan = isExpiredPlanState(planState);

  if (!isExpiredPlan) {
    return (
      <Col className="gap-lg" data-testid="SubscriptionPlan-Guidance">
        <div className={cn("rounded-xl border border-solid px-4 py-3 flex gap-3", colors.border, colors.background)}>
          <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className={cn(colors.text, "shrink-0 mt-0.5")} />
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className={colors.text}>
            <Trans i18nKey="subscription:overview.oneTimeInfo" components={ALERT_BOLD_COMPONENT} />
          </Text>
        </div>
        <Row align="start" className="gap-2">
          <Icon icon={IconVariants.ROTATE_CW} size={IconSize.SMALL} className="text-gray-11 shrink-0 mt-0.5" />
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-11">
            <Trans i18nKey="subscription:overview.afterExpiryNote" />
          </Text>
        </Row>
      </Col>
    );
  }

  return (
    <Col className="gap-lg" data-testid="SubscriptionPlan-Guidance">
      <div className={cn("rounded-xl border border-solid px-4 py-3 flex gap-3", colors.border, colors.background)}>
        <Icon icon={IconVariants.CIRCLE_ALERT} size={IconSize.SMALL} className={cn(colors.text, "shrink-0 mt-0.5")} />
        <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-red-11">
          <Trans i18nKey="subscription:overview.expired.inCardAlert" components={ALERT_BOLD_COMPONENT} />
        </Text>
      </div>
      <Row align="start" className="gap-2">
        <Icon icon={IconVariants.ROTATE_CW} size={IconSize.SMALL} className="text-gray-11 shrink-0 mt-0.5" />
        <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-11">
          <Trans i18nKey="subscription:overview.expired.restoreAccessNote" />
        </Text>
      </Row>
    </Col>
  );
};

export default SubscriptionPlanGuidance;
